import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import assert from 'assert';
import classNames from 'classnames';
import React from 'react';

import {deleteAnalysisSteps, runAnalysisStep} from 'analysis-tool/AnalysisActions';
import {
  MULTIPLE_CHARTS_OUTPUT_STEP_DATA_NAME,
  SaveToResultsStepDataName,
  SendToDocumentStepDataName,
  SINGLE_CHART_OUTPUT_STEP_DATA_NAME,
  SINGLE_DATASET_OUTPUT_STEP_DATA_NAME,
} from 'analysis-tool/AnalysisStepDataNames';
import {ANALYSIS_TOOL_SPACING} from 'analysis-tool/AnalysisToolConstants';
import {
  AnalysisGraphQL,
  GetAnalysisPipelineStep,
  GetAnalysisChart,
  isAnalysisGraphQL,
} from 'analysis-tool/AnalysisTypes';
import {
  findAndAssertSingleStepChart,
  findAndAssertStepDataCharts,
} from 'analysis-tool/data/findAndAssertStepDataCharts';
import {findAndAssertSingleStepDataFrameId} from 'analysis-tool/data/findAndAssertStepDataFrameIds';
import DeleteFailedSaveToResultsStepBanner from 'analysis-tool/structured-data-mapping/DeleteFailedSaveToResultsStepBanner';
import SavedResultsQuery from 'analysis-tool/structured-data-mapping/SavedResultsQuery';
import useLogAnalysisProfilerTimingFinished from 'analysis-tool/ui/hooks/useLogAnalysisProfilerTimingFinished';
import {AnalysisMainViewItemType, MainViewComponentProps} from 'analysis-tool/ui/main-view/MainViewTypes';
import isArchived from 'archive/isArchived';
import {QueryingChartContent} from 'charts/chart-content/ChartContent';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import PersistedDataFrameTableWrapper from 'data-frames/wrapper/PersistedDataFrameTableWrapper';
import {
  ActionOrigin,
  PipelineStepStatusCode,
  PipelineStepType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import navigateToURL from 'lib/navigateToURL';
import {standaloneProps} from 'notebook/simple-tables/SimpleTableEditable';
import {SEND_TO_DOCUMENT_FINISHED_EVENT} from 'performance/processAnalysesEvents';
import {DeletePipelineStepsBehavior} from 'pipelines/network/PipelineDAO';
import {getParentSteps} from 'pipelines/StepLineageUtils';
import {getPipelineStepConfig} from 'pipelines/StepShapeUtils';
import AssayResultsTable from 'runs/AssayResultsTable';
import {FONT_PROXIMA_11, FONT_PROXIMA_18} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import runConfirmDeleteModal from 'ui-library/modals/runConfirmDeleteModal';
import Pane from 'ui-library/pane/Pane';
import {withLoadingScreen} from 'util/Promises';

import DocumentName from './DocumentName';
import {getDocumentIdFromStep, getSendToDocumentStepConfig} from './OutputsUtil';
import useDocumentMetadata from './useDocumentMetadata';

export const SEND_TO_DOCUMENT_VIEW_RESULT_ROW_LIMIT = 50;

interface SendToDocumentHeaderProps {
  analysis: AnalysisGraphQL;
  analysisClientStateId: string;
  step: GetAnalysisPipelineStep;
}

export function SendToDocumentHeader({
  analysis,
  analysisClientStateId,
  step,
}: SendToDocumentHeaderProps): JSX.Element {
  const {documentType, editUrl} = useDocumentMetadata(getDocumentIdFromStep(step));

  // Refer to both `DOCUMENT` and `WORKSHEET` as 'worksheet', since documents are steps within worksheets
  const documentText = documentType === ModelTypeConstants.ENTRY ? 'entry' : 'worksheet';

  const documentName = (
    <div className={css(styles.documentName)}>
      <DocumentName step={step} />
    </div>
  );

  switch (step.statusCode) {
    case PipelineStepStatusCode.SUCCEEDED:
      return (
        <div {...getTestProps({component: 'SendToDocumentView', element: 'root'})}>
          <div className={css(styles.helpText)}>Sent to</div>
          <div className={css(styles.headerLineWrapper)}>
            <BenchlingIcon icon={documentText} className={css(styles.documentIcon)} />
            <Button
              icon="open"
              className={css(styles.openDocumentButton)}
              buttonStyle={ButtonStyle.LINK}
              onClick={() => {
                if (editUrl) {
                  navigateToURL(editUrl);
                }
              }}
              dataTestAttrs={{component: 'SendToDocumentView', element: 'openDocumentButton'}}
              disabled={editUrl == null}
            >
              <div className="u-mini-spacing-right">{documentName}</div>
            </Button>
          </div>
        </div>
      );
    case PipelineStepStatusCode.FAILED:
      return (
        <>
          <div className={classNames(css(styles.headerLineWrapper), 'u-mini-spacing-bottom')}>
            <BenchlingIcon icon={documentText} className={css(styles.documentIcon)} />
            {documentName}
          </div>
          <InlineBanner
            bannerType={InlineBannerType.ERROR}
            text={`Error sending to ${documentText}`}
            supportingInfo={step.statusMessage ?? ''}
          />
          <Button
            className="u-small-spacing-top"
            buttonStyle={ButtonStyle.PRIMARY}
            disabled={isArchived(analysis)}
            icon="rotate-right"
            onClick={() =>
              withLoadingScreen(runAnalysisStep(analysis.id, {stepId: step.id}), 'Retrying send data...')
            }
            size={ButtonSize.SMALL}
            text="Retry send data"
          />
          <Button
            className="u-small-spacing-top u-mini-spacing-left"
            buttonStyle={ButtonStyle.NEGATIVE}
            disabled={isArchived(analysis)}
            icon="delete"
            onClick={() => {
              runConfirmDeleteModal({typeName: 'failed sending data'}).then((confirmed) => {
                if (!confirmed) {
                  return;
                }
                withLoadingScreen(
                  deleteAnalysisSteps(
                    analysis,
                    {
                      stepIds: [step.id],
                      behavior: DeletePipelineStepsBehavior.NO_INFERENCE,
                    },
                    analysisClientStateId
                  ),
                  'Deleting send data...'
                );
              });
            }}
            size={ButtonSize.SMALL}
            text="Delete send data"
          />
        </>
      );
    case PipelineStepStatusCode.RUNNING:
    case PipelineStepStatusCode.AI_PROPOSED:
    case PipelineStepStatusCode.WAITING:
    case PipelineStepStatusCode.BLOCKED:
      return (
        <>
          <div className={css(styles.headerLineWrapper)}>
            <BenchlingIcon
              icon="hourglass-half"
              className={classNames(css(styles.hourglassIcon), 'u-mini-spacing-right')}
            />
            <div className={css(styles.helpText)}>Sending to...</div>
          </div>
          <div className={css(styles.headerLineWrapper)}>
            <BenchlingIcon icon={documentText} className={css(styles.documentIcon)} />
            {documentName}
          </div>
        </>
      );
    case PipelineStepStatusCode.HALTED:
      return (
        <div>
          <div className={css(styles.helpText)}>Halted</div>
          <div className={css(styles.headerLineWrapper)}>
            <BenchlingIcon icon={documentText} className={css(styles.documentIcon)} />
            {documentName}
          </div>
        </div>
      );
    default:
      throw new Error(`Unexpected step status for send to document: ${step.statusCode}`);
  }
}

/**
 * To get the selected UDF charts, we need to use the step inputReferences to know which charts were selected
 */
function getSelectedUDFCharts(
  analysis: AnalysisGraphQL,
  step: GetAnalysisPipelineStep,
  parentStep: GetAnalysisPipelineStep,
  chartInputReferences: Array<{inputDataIndex: number}>
): Array<GetAnalysisChart> {
  const chartParentSteps = getParentSteps(analysis, step).filter((step) =>
    [
      PipelineStepType.USER_DEFINED_FUNCTION,
      PipelineStepType.CHART_VIEW,
      PipelineStepType.REGRESSION,
    ].includes(step.definition.stepType)
  );

  const allChartsFlattened: Array<{
    chart: GetAnalysisChart;
    parentStep: GetAnalysisPipelineStep;
  }> = [];
  chartParentSteps.forEach((chartStep) => {
    if (chartStep.definition.stepType === PipelineStepType.USER_DEFINED_FUNCTION) {
      const stepCharts = findAndAssertStepDataCharts(
        chartStep,
        MULTIPLE_CHARTS_OUTPUT_STEP_DATA_NAME,
        false,
        // UDFs may not always output N_CHARTS > 1, so we should not enforce charts' existence
        true
      );
      stepCharts.forEach((chart) => allChartsFlattened.push({chart, parentStep: chartStep}));
    } else {
      const chart = findAndAssertSingleStepChart(chartStep, SINGLE_CHART_OUTPUT_STEP_DATA_NAME, false);
      allChartsFlattened.push({chart, parentStep: chartStep});
    }
  });

  return chartInputReferences
    .map((ref) => allChartsFlattened[ref.inputDataIndex])
    .filter((item) => item && item.parentStep === parentStep)
    .map((item) => item.chart);
}

export type SendToDocumentViewProps = MainViewComponentProps<AnalysisMainViewItemType.SEND_TO_DOCUMENT>;

export default function SendToDocumentView({
  analysis,
  step,
  analysisClientStateId,
  clientStateId,
}: SendToDocumentViewProps): JSX.Element {
  assert(isAnalysisGraphQL(analysis), 'SendToDocumentView only supports analyses.');
  useLogAnalysisProfilerTimingFinished(
    step.statusCode === PipelineStepStatusCode.WAITING || step.statusCode === PipelineStepStatusCode.RUNNING,
    [SEND_TO_DOCUMENT_FINISHED_EVENT]
  );
  const allParentSteps = getParentSteps(analysis, step).filter((step) =>
    [
      PipelineStepType.SAVE_TO_RESULTS,
      PipelineStepType.EXPORT_DATA_CATALOG_DATASET,
      PipelineStepType.USER_DEFINED_FUNCTION,
      PipelineStepType.CHART_VIEW,
      PipelineStepType.REGRESSION,
    ].includes(step.definition.stepType)
  );

  // Deduplicate only UDF steps since they can have multiple chart outputs, but keep all instances of other step types
  const parentSteps = allParentSteps.filter((step, index, array) => {
    if (step.definition.stepType === PipelineStepType.USER_DEFINED_FUNCTION) {
      return array.findIndex((s) => s.id === step.id) === index;
    }
    return true;
  });

  return (
    <div
      className={css(styles.container)}
      {...getTestProps({component: 'SendToDocumentView', element: 'root'})}
    >
      <div className={css(styles.headerWrapper)}>
        <SendToDocumentHeader analysis={analysis} analysisClientStateId={analysisClientStateId} step={step} />
      </div>
      <div className={css(styles.outputsContainer)}>
        {/* oxlint-disable-next-line array-callback-return */}
        {parentSteps.map((parentStep, i) => {
          switch (parentStep.statusCode) {
            case PipelineStepStatusCode.SUCCEEDED: {
              if (parentStep.definition.stepType === PipelineStepType.SAVE_TO_RESULTS) {
                return (
                  <SavedResultsQuery
                    dataFrameId={findAndAssertSingleStepDataFrameId(
                      parentStep,
                      SaveToResultsStepDataName.SAVED_RESULTS
                    )}
                    resultSchemaId={
                      getPipelineStepConfig(parentStep, PipelineStepType.SAVE_TO_RESULTS, 2).schemaId
                    }
                    rowLimit={SEND_TO_DOCUMENT_VIEW_RESULT_ROW_LIMIT}
                    key={`saved-results-${parentStep.id}`}
                  >
                    {(totalNumSavedResults, results, resultSchema) => (
                      <>
                        {totalNumSavedResults > results.length && (
                          <div className={css(styles.resultLimitInfoBanner)}>
                            <InlineBanner
                              bannerType={InlineBannerType.WARNING}
                              text={`Showing the first ${SEND_TO_DOCUMENT_VIEW_RESULT_ROW_LIMIT} Results of ${totalNumSavedResults}.`}
                            />
                          </div>
                        )}
                        <div
                          className={classNames(
                            'mediocre',
                            'react-tab-container--mediocre-tableWrapper',
                            'react-tab-container--mediocre-tableWrapper--noPaddingTop'
                          )}
                        >
                          <div className="mediocre-item is-table">
                            <AssayResultsTable
                              additionalColumns={[]}
                              attachedForReview={false}
                              forceRowCount={totalNumSavedResults}
                              results={results}
                              schema={resultSchema}
                              canRename={false}
                              updateDisplayConfigHandler={() => {}}
                              {...standaloneProps}
                              fileId=""
                              cid={clientStateId}
                              locked={true}
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </SavedResultsQuery>
                );
              } else if (parentStep.definition.stepType === PipelineStepType.EXPORT_DATA_CATALOG_DATASET) {
                return (
                  <PersistedDataFrameTableWrapper
                    actionOrigin={ActionOrigin.ANALYSIS_TOOL}
                    clientStateId={clientStateId}
                    dataFrameId={findAndAssertSingleStepDataFrameId(
                      parentStep,
                      SINGLE_DATASET_OUTPUT_STEP_DATA_NAME
                    )}
                    key={`dataset-table-${i}`}
                  />
                );
              } else if (parentStep.definition.stepType === PipelineStepType.USER_DEFINED_FUNCTION) {
                const stepConfig = getSendToDocumentStepConfig(step);
                const chartInputReferences = stepConfig.sectionLayout.flatMap((section) =>
                  section.inputReferences.filter(
                    (ref) => ref.inputDataName === SendToDocumentStepDataName.INPUT_CHARTS
                  )
                );

                const selectedChartsFromThisStep = getSelectedUDFCharts(
                  analysis,
                  step,
                  parentStep,
                  chartInputReferences
                );

                return selectedChartsFromThisStep.map((chart, chartIndex) => (
                  <Pane title={chart.name} key={`chart-${i}-${chartIndex}`}>
                    <div className={css(styles.chartPreview)}>
                      <QueryingChartContent chartId={chart.id} />
                    </div>
                  </Pane>
                ));
              } else {
                assert(
                  [PipelineStepType.CHART_VIEW, PipelineStepType.REGRESSION].includes(
                    parentStep.definition.stepType
                  )
                );
                const chart = findAndAssertSingleStepChart(
                  parentStep,
                  SINGLE_CHART_OUTPUT_STEP_DATA_NAME,
                  false
                );
                return (
                  <Pane title={chart.name} key={`chart-${i}`}>
                    <div className={css(styles.chartPreview)}>
                      <QueryingChartContent chartId={chart.id} />
                    </div>
                  </Pane>
                );
              }
            }
            case PipelineStepStatusCode.FAILED:
              assert(
                parentStep.statusMessage != null,
                'Expected statusMessage to be defined for FAILED step.'
              );
              return (
                <DeleteFailedSaveToResultsStepBanner
                  deleteStep={
                    analysis.permissions.writable
                      ? () =>
                          deleteAnalysisSteps(
                            analysis,
                            {
                              stepIds: [parentStep.id],
                              behavior: DeletePipelineStepsBehavior.DELETE_ALL_CHILDREN,
                            },
                            analysisClientStateId
                          )
                      : undefined
                  }
                  supportingInfo={parentStep.statusMessage}
                  key={`saved-results-${parentStep.id}`}
                />
              );
            case PipelineStepStatusCode.RUNNING:
            case PipelineStepStatusCode.WAITING:
            case PipelineStepStatusCode.BLOCKED:
              // TODO: Show in-progress save to results steps
              return <></>;
            case PipelineStepStatusCode.HALTED:
              return null;
            default:
              throw new Error(`Unexpected step status for SAVE_TO_RESULTS: ${parentStep.statusCode}`);
          }
        })}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  chartPreview: {
    height: '400px',
  },
  openDocumentButton: {
    display: 'flex',
    flexDirection: 'row-reverse',
    alignItems: 'center',
  },
  headerWrapper: {
    marginBottom: '12px',
  },
  resultLimitInfoBanner: {
    marginBottom: '12px',
  },
  headerLineWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  helpText: {...FONT_PROXIMA_11, color: Color.GRAY_600},
  documentName: FONT_PROXIMA_18,
  documentIcon: {
    color: Color.GRAY_600,
    marginRight: '10px',
  },
  hourglassIcon: {
    color: Color.BLUE_450,
  },
  container: {
    margin: ANALYSIS_TOOL_SPACING,
  },
  outputsContainer: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '12px',
  },
});
